/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import {
  Cpu,
  Shield,
  Droplets,
  Hammer,
  Heart,
  Sparkles,
  Plus,
  CheckCircle2,
  Clock,
  Users as UsersIcon,
  ArrowLeft,
  Crown,
  X,
  Search,
  UserPlus,
} from "lucide-react";
import { Avatar, Button, Dialog, EmptyState, Pill, toast } from "../ui";
import { boardApi } from "./api";
import MemberCard from "./MemberCard";
import { resolveAvatarUrl } from "../utils/avatarUrl";
import useDebouncedValue from "../utils/useDebouncedValue";
import "./TeamsTab.css";

const ICON_MAP = {
  Cpu, Shield, Droplets, Hammer, Heart, Sparkles, Users: UsersIcon,
};
function teamIcon(team) {
  return ICON_MAP[team.icon] || UsersIcon;
}

export default function TeamsTab({ isAdmin }) {
  const [view, setView] = useState({ name: "list", slug: null });
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = () => setRefreshKey((k) => k + 1);
  return view.name === "list" ? (
    <TeamsList
      isAdmin={isAdmin}
      onOpenTeam={(slug) => setView({ name: "detail", slug })}
      onChanged={reload}
      reloadKey={refreshKey}
    />
  ) : (
    <TeamDetail
      slug={view.slug}
      isAdmin={isAdmin}
      onBack={() => setView({ name: "list", slug: null })}
      onChanged={reload}
    />
  );
}

function TeamsList({ isAdmin, onOpenTeam, onChanged, reloadKey }) {
  const [teams, setTeams] = useState([]);
  const [myTeamSlug, setMyTeamSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [pending, setPending] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await boardApi.listTeams();
      // Backwards-compat: handle either {teams,my_team_slug} or a plain
      // array if any caller is still on the old shape.
      if (Array.isArray(r)) {
        setTeams(r);
        setMyTeamSlug(null);
      } else {
        setTeams(r?.teams || []);
        setMyTeamSlug(r?.my_team_slug || null);
      }
      if (isAdmin) {
        try {
          setPending(await boardApi.listPendingApplications());
        } catch (_) {}
      }
    } catch (e) {
      setError(e.message || "Could not load");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  return (
    <>
      <div className="vst-row">
        <div className="vst-blurb">
          <Sparkles size={16} strokeWidth={1.9} />
          <span>
            Volunteer teams help the Board with day-to-day operations.
            Join any one team — you can leave any time.
            {myTeamSlug && (
              <>
                {" "}You're currently on{" "}
                <strong>
                  {teams.find((t) => t.slug === myTeamSlug)?.name ||
                    "another team"}
                </strong>
                .
              </>
            )}
          </span>
        </div>
        {isAdmin && (
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => setShowCreate(true)}
          >
            New team
          </Button>
        )}
      </div>

      {isAdmin && pending.length > 0 && (
        <PendingPanel
          rows={pending}
          onChanged={() => {
            load();
            onChanged && onChanged();
          }}
        />
      )}

      {error && <div className="vsbd-error">{error}</div>}
      {loading && <div className="vsbd-empty">Loading…</div>}

      {!loading && teams.length === 0 && (
        <EmptyState
          icon={<UsersIcon size={28} strokeWidth={1.4} />}
          title="No volunteer teams yet"
          subtitle={
            isAdmin
              ? "Create the first team (IT, Security, Water Quality…) above."
              : "An admin will spin them up soon."
          }
        />
      )}

      {!loading && teams.length > 0 && (
        <div className="vst-grid">
          {teams.map((t) => {
            const Icon = teamIcon(t);
            return (
              <button
                key={t.id}
                className="vst-tile"
                onClick={() => onOpenTeam(t.slug)}
              >
                <div
                  className="vst-tile-icon"
                  style={{
                    background: t.color ? t.color + "22" : "var(--accent-light)",
                    color: t.color || "var(--accent)",
                  }}
                >
                  <Icon size={20} strokeWidth={2.1} />
                </div>
                <div className="vst-tile-body">
                  <h4>{t.name}</h4>
                  {t.description && (
                    <p className="vst-tile-desc">{t.description}</p>
                  )}
                  <div className="vst-tile-foot">
                    {t.lead ? (
                      <span className="vst-tile-lead">
                        <Crown size={11} /> Lead: {t.lead.name}
                      </span>
                    ) : (
                      <span className="vst-tile-no-lead">No lead yet</span>
                    )}
                    <span className="vst-tile-count">
                      {t.member_count}{" "}
                      {t.member_count === 1 ? "member" : "members"}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateTeamModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}
    </>
  );
}

function PendingPanel({ rows, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const act = async (id, action) => {
    setBusyId(id);
    try {
      if (action === "approve") await boardApi.approveApplication(id);
      else await boardApi.rejectApplication(id);
      onChanged && onChanged();
    } catch (e) {
      toast.danger(e.message || "Could not save");
    } finally {
      setBusyId(null);
    }
  };
  return (
    <div className="vst-pending">
      <div className="vst-pending-head">
        <Clock size={14} />{" "}
        <strong>
          {rows.length} pending application{rows.length === 1 ? "" : "s"}
        </strong>
      </div>
      <ul>
        {rows.map((r) => (
          <li key={r.id}>
            <div className="vst-pending-info">
              <Avatar
                name={r.applicant.name || ""}
                imageUrl={
                  r.applicant.avatar_url
                    ? resolveAvatarUrl(r.applicant.avatar_url)
                    : undefined
                }
                size={32}
              />
              <div>
                <strong>{r.applicant.name}</strong> →{" "}
                <span>{r.team.name}</span>
                {r.applied_note && (
                  <div className="vst-pending-note">"{r.applied_note}"</div>
                )}
              </div>
            </div>
            <div className="vst-pending-actions">
              <Button
                variant="secondary"
                size="sm"
                disabled={busyId === r.id}
                onClick={() => act(r.id, "reject")}
              >
                Reject
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={busyId === r.id}
                disabled={busyId === r.id}
                onClick={() => act(r.id, "approve")}
              >
                Approve
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TeamDetail({ slug, isAdmin, onBack, onChanged }) {
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [addingId, setAddingId] = useState(null);
  const debouncedAddQuery = useDebouncedValue(addQuery, 300);

  // Admin typeahead: search residents to add to this team directly.
  useEffect(() => {
    let cancelled = false;
    const q = debouncedAddQuery.trim();
    if (!isAdmin || q.length < 2) {
      setAddResults([]);
      return;
    }
    boardApi
      .searchTeamCandidates(q)
      .then((d) => {
        if (!cancelled) setAddResults(d.results || []);
      })
      .catch(() => {
        if (!cancelled) setAddResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedAddQuery, isAdmin]);

  const onAddMember = async (userId) => {
    setAddingId(userId);
    try {
      await boardApi.addTeamMember(slug, userId);
      setAddQuery("");
      setAddResults([]);
      toast.success("Member added");
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.danger(e.message);
    } finally {
      setAddingId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setTeam(await boardApi.getTeam(slug));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const onJoin = async () => {
    setBusy(true);
    try {
      await boardApi.joinTeam(slug, null);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.danger(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onLeave = async () => {
    if (
      !window.confirm(
        "Leave this team? You'll stop getting meeting invites for it."
      )
    )
      return;
    setBusy(true);
    try {
      await boardApi.leaveTeam(slug, null);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.danger(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="vsbd-empty">Loading…</div>;
  if (error) return <div className="vsbd-error">{error}</div>;
  if (!team) return null;

  const Icon = teamIcon(team);
  const status = team.my_membership?.status;
  const iAmMember = status === "APPROVED";

  return (
    <>
      <button className="vst-back" onClick={onBack}>
        <ArrowLeft size={13} /> All teams
      </button>

      <div className="vst-detail-head">
        <div
          className="vst-tile-icon"
          style={{
            background: team.color ? team.color + "22" : "var(--accent-light)",
            color: team.color || "var(--accent)",
          }}
        >
          <Icon size={26} strokeWidth={2.1} />
        </div>
        <div>
          <h2>{team.name}</h2>
          {team.description && <p>{team.description}</p>}
        </div>
        {!isAdmin && (
          <div className="vst-head-cta">
            {iAmMember ? (
              <div className="vst-mem-actions">
                <Pill variant="success" className="vst-member-pill">
                  <CheckCircle2 size={14} /> You're on this team
                </Pill>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onLeave}
                  loading={busy}
                  disabled={busy}
                >
                  Leave team
                </Button>
              </div>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={onJoin}
                loading={busy}
                disabled={busy}
              >
                Join team
              </Button>
            )}
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="vst-add-member">
          <div className="vst-add-search">
            <Search size={15} />
            <input
              type="text"
              placeholder="Add a resident to this team…"
              value={addQuery}
              onChange={(e) => setAddQuery(e.target.value)}
            />
          </div>
          {addResults.length > 0 && (
            <div className="vst-add-results">
              {addResults.map((r) => (
                <button
                  key={r.id}
                  className="vst-add-result"
                  disabled={addingId === r.id}
                  onClick={() => onAddMember(r.id)}
                >
                  <Avatar
                    name={r.name || ""}
                    imageUrl={
                      r.avatar_url ? resolveAvatarUrl(r.avatar_url) : undefined
                    }
                    size={28}
                  />
                  <span className="vst-add-result-name">{r.name}</span>
                  <UserPlus size={14} className="vst-add-result-icon" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {team.members && team.members.length === 0 ? (
        <EmptyState
          icon={<UsersIcon size={26} strokeWidth={1.4} />}
          title="No members yet"
          subtitle={isAdmin ? "Add a resident above." : "Be the first to apply!"}
        />
      ) : (
        <div className="vsbd-grid">
          {team.members?.map((m) => (
            <MemberCard
              key={m.id}
              variant="team"
              member={m}
              isAdmin={isAdmin}
              onPromote={async () => {
                try {
                  await boardApi.promoteLead(slug, m.user.id);
                  load();
                } catch (e) {
                  toast.danger(e.message);
                }
              }}
              onRemove={async () => {
                if (
                  !window.confirm(`Remove ${m.user.name} from ${team.name}?`)
                )
                  return;
                try {
                  await boardApi.removeMember(slug, m.user.id);
                  load();
                } catch (e) {
                  toast.danger(e.message);
                }
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}

function CreateTeamModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("Users");
  const [color, setColor] = useState("#1B5E20"); // brand-color-ok: color input requires hex
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      await boardApi.createTeam({ name: name.trim(), description: description.trim() || null, icon, color });
      onCreated && onCreated();
    } catch (err) {
      setError(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={true}
      onOpenChange={(o) => !o && onClose && onClose()}
      title="New volunteer team"
      className="vsbf-modal"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="vsbf-create-team-form"
            variant="primary"
            loading={busy}
            disabled={busy || !name.trim()}
          >
            Create team
          </Button>
        </>
      }
    >
      <form id="vsbf-create-team-form" onSubmit={submit} className="vsbf-form">
        <div className="vsbf-field">
          <label>Name *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="IT Team"
            required
            minLength={2}
            maxLength={128}
          />
        </div>
        <div className="vsbf-field">
          <label>Description</label>
          <textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this team handles…"
          />
        </div>
        <div className="vsbf-grid">
          <div className="vsbf-field">
            <label>Icon (Lucide name)</label>
            <select value={icon} onChange={(e) => setIcon(e.target.value)}>
              {Object.keys(ICON_MAP).map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
          <div className="vsbf-field">
            <label>Accent color</label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </div>
        </div>
        {error && <div className="vsbf-error">{error}</div>}
      </form>
    </Dialog>
  );
}
